'use client'

import React from 'react'
import { Video, Home, MapPin, FileText, Icon } from 'react-feather'
import { cn } from '@/lib/utils'
import { AvailabilityMeetingPlace } from '@/core/graphql/types'
import { t } from '@lingui/core/macro'

const meetingPlaceConfig: Record<
  string,
  { icon: Icon; bgColor: string; textColor: string; borderColor: string }
> = {
  [AvailabilityMeetingPlace.VisioConference]: {
    icon: Video,
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    borderColor: 'border-indigo-200',
  },
  [AvailabilityMeetingPlace.AtHome]: {
    icon: Home,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
  },
  [AvailabilityMeetingPlace.Center]: {
    icon: MapPin,
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    borderColor: 'border-teal-200',
  },
  [AvailabilityMeetingPlace.SecondOpinion]: {
    icon: FileText,
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
  },
}

const defaultConfig: { icon: Icon; bgColor: string; textColor: string; borderColor: string } = {
  icon: MapPin,
  bgColor: 'bg-gray-50',
  textColor: 'text-gray-600',
  borderColor: 'border-gray-200',
}

interface MeetingPlaceBadgeProps {
  placeId: string | null | undefined
  className?: string
}

function getMeetingPlaceLabel(placeId: string | null | undefined): string {
  switch (placeId) {
    case AvailabilityMeetingPlace.VisioConference: return t`Video visit`
    case AvailabilityMeetingPlace.AtHome: return t`At home`
    case AvailabilityMeetingPlace.Center: return t`At clinic`
    case AvailabilityMeetingPlace.SecondOpinion: return t`Second Opinion`
    default: return placeId || ''
  }
}

export function MeetingPlaceBadge({ placeId, className }: MeetingPlaceBadgeProps) {
  const config = (placeId && meetingPlaceConfig[placeId]) || defaultConfig
  const IconComponent = config.icon
  const label = getMeetingPlaceLabel(placeId)

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border',
        config.bgColor,
        config.textColor,
        config.borderColor,
        className,
      )}
    >
      <IconComponent size={12} />
      <span>{label}</span>
    </span>
  )
}
